import { Link as RouterLink } from 'react-router-dom';

// material-ui
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

// assets
import MenuFoldOutlined from '@ant-design/icons/MenuFoldOutlined';
import MenuUnfoldOutlined from '@ant-design/icons/MenuUnfoldOutlined';
import bhsLogo from 'assets/images/brand/bhs-logo.svg';
import { Language, translations } from '../../../i18n';
import { useTranslation } from 'contexts/LanguageContext';

type HeaderProps = {
  simple?: boolean;
  drawerOpen?: boolean;
  drawerOffset?: number;
  onDrawerToggle?: () => void;
  onMobileMenuToggle?: () => void;
  showBrand?: boolean;
};

export default function Header({
  simple = false,
  drawerOpen = true,
  drawerOffset = 0,
  onDrawerToggle,
  onMobileMenuToggle,
  showBrand = true
}: HeaderProps) {
  const { language, setLanguage, t } = useTranslation();

  const languageSwitch = (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        gap: 0.5,
        p: 0.25,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        flexShrink: 0
      }}
    >
      {(Object.keys(translations) as Language[]).map((item) => (
        <Button
          key={item}
          size="small"
          variant={language === item ? 'contained' : 'text'}
          color={language === item ? 'primary' : 'secondary'}
          onClick={() => setLanguage(item)}
          sx={{
            minWidth: { xs: 28, sm: 32 },
            minHeight: 28,
            px: { xs: 0.65, sm: 0.9 },
            py: 0.25,
            fontSize: '0.75rem',
            lineHeight: 1,
            boxShadow: 'none'
          }}
        >
          {item}
        </Button>
      ))}
    </Stack>
  );

  const userActions = !simple && (
    <Stack direction="row" sx={{ alignItems: 'center', gap: { xs: 0.4, sm: 1.25 }, minWidth: 0, flexShrink: 1 }}>
      <Avatar sx={{ width: { xs: 26, sm: 30 }, height: { xs: 26, sm: 30 }, bgcolor: 'primary.lighter', color: 'primary.dark', fontSize: 13 }}>A</Avatar>
      <Stack sx={{ alignItems: 'flex-start', minWidth: 0, maxWidth: { xs: 54, sm: 92 } }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1, display: { xs: 'none', sm: 'block' } }}>
          {t('Welcome')}
        </Typography>
        <Typography variant="subtitle2" sx={{ lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
          {t('Admin')}
        </Typography>
      </Stack>
    </Stack>
  );

  return (
    <AppBar
      position="fixed"
      color="inherit"
      elevation={0}
      sx={(theme) => ({
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
        bgcolor: 'background.paper',
        ...(simple
          ? {}
          : {
              left: { lg: drawerOffset },
              width: { lg: `calc(100% - ${drawerOffset}px)` },
              transition: theme.transitions.create(['left', 'width'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.shorter
              })
            })
      })}
    >
      <Container maxWidth={false} sx={{ px: { xs: 1.25, sm: 3 } }}>
        <Toolbar
          disableGutters
          sx={{
            minHeight: 74,
            py: 0,
            gap: { xs: 0.5, sm: 2 },
            alignItems: 'center',
            flexWrap: 'nowrap'
          }}
        >
          <Stack direction="row" sx={{ alignItems: 'center', gap: { xs: 0.5, sm: 2 }, minWidth: 0, flex: '0 1 auto' }}>
            {!simple && (
              <>
                <IconButton
                  aria-label={t('Open menu')}
                  color="secondary"
                  onClick={onMobileMenuToggle}
                  sx={{
                    display: { xs: 'inline-flex', lg: 'none' },
                    flexShrink: 0,
                    width: { xs: 24, sm: 32 },
                    height: 32,
                    p: 0,
                    justifyContent: 'flex-start',
                    borderRadius: 1
                  }}
                >
                  <MenuUnfoldOutlined />
                </IconButton>
                <IconButton
                  aria-label={drawerOpen ? 'Collapse menu' : 'Expand menu'}
                  color="secondary"
                  onClick={onDrawerToggle}
                  sx={{
                    display: { xs: 'none', lg: 'inline-flex' },
                    flexShrink: 0,
                    width: 32,
                    height: 32,
                    p: 0,
                    justifyContent: 'flex-start',
                    borderRadius: 1
                  }}
                >
                  {drawerOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
                </IconButton>
              </>
            )}

            {showBrand && (
              <Stack
                component={RouterLink}
                to={simple ? '/' : '/hotels/details'}
                direction="row"
                sx={{
                  alignItems: 'center',
                  gap: { xs: 1.25, sm: 2 },
                  textDecoration: 'none',
                  display: simple ? 'flex' : { xs: 'flex', lg: 'none' },
                  minWidth: 0
                }}
              >
                <Box
                  component="img"
                  src={bhsLogo}
                  alt="Business Hotel Service International"
                  sx={{
                    width: { xs: simple ? 42 : 38, sm: 54 },
                    height: { xs: simple ? 42 : 38, sm: 54 },
                    flexShrink: 0,
                    display: 'block'
                  }}
                />
                <Box sx={{ display: { xs: simple ? 'block' : 'none', sm: 'block' }, minWidth: 0 }}>
                  <Typography
                    variant="h5"
                    sx={{ color: 'text.primary', lineHeight: 1.25, fontWeight: 500, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}
                  >
                    Business Hotel Service
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ color: 'text.primary', lineHeight: 1.25, fontWeight: 500, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}
                  >
                    {t('International')}
                  </Typography>
                </Box>
              </Stack>
            )}
          </Stack>

          <Box sx={{ flexGrow: 1, minWidth: { xs: 2, sm: 16 } }} />

          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: { xs: 0.4, sm: 1.25 },
              ml: 'auto',
              width: 'auto',
              justifyContent: 'flex-end',
              minWidth: 0,
              flexShrink: 1
            }}
          >
            {languageSwitch}
            {userActions}
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  );
}


